/**
 * The plan's money rule, in one place.
 *
 *     rate    = round(demo (000s) × CPM)     per spot, whole dollars
 *     dollars = rate × units
 *
 * ⚠ Do NOT "improve" this to `round(demo × cpm × units)`. Rounding once at the
 * end is more precise in isolation, but Salesforce derives its own total as
 * `Rate__c × Units_Available__c` from the rounded rate we push it. Rounding
 * once puts every fractional-rate row ~$1 off the Salesforce record it maps
 * to, and MPB exists to be checkable against Salesforce. Ties beat precision.
 *
 * A consequence worth knowing before someone files it as a bug: the Rate
 * column × Units will not reproduce Total $ to the penny, because the rate is
 * a whole dollar and the CPM behind it is not. That is the trade, made
 * deliberately.
 *
 * ARITHMETIC ONLY — no null/zero guards live here. Callers keep their own,
 * because saved deals and added rows guard differently (`effDemo != null`
 * versus a truthiness check on the demo). A row with a demo of 0 therefore
 * prices at $0 as a saved deal but blocks Save as an added row. That split
 * predates this module and is deliberately left alone; folding the guards
 * together would quietly change which rows block Save.
 *
 * Before this existed the arithmetic was written out thirteen times, and two
 * of those copies had drifted into a different formula.
 */

/** Per-spot rate in whole dollars. */
export function perSpotRate(demo000: number, cpm: number): number {
  return Math.round(demo000 * cpm);
}

/** Row total. Null rate (unpriced row) contributes nothing. */
export function rowDollars(rate: number | null | undefined, units: number): number {
  return rate != null ? rate * units : 0;
}
